import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useStrikePrices, strikeCls } from "@/lib/pricing";
import { Empty } from "@/pages/Dashboard";
import { Lock, Unlock, IndianRupee, Ship, Building2, Anchor, MessageCircle, Share2, Users, CheckCircle2, Gift, Lightbulb, Download, X } from "lucide-react";
import { payFor } from "@/lib/pay";
import { MatchAlerts } from "@/components/MatchAlerts";
import { GetNoticedFaster } from "@/components/GetNoticedFaster";
import { buildContactMessage, waLink } from "@/lib/waMessage";

const Field = ({ label, value, tag }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-[#8A8F9E] flex items-center gap-1.5 flex-wrap">
      {label}
      {tag && (
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${tag === "Mandatory" ? "bg-[#ef4444]/15 text-[#ff6b6b] border-[#ef4444]/40" : "bg-[#DFFF00]/15 text-[#DFFF00] border-[#DFFF00]/40"}`}>
          {tag}
        </span>
      )}
    </div>
    <div className="text-sm font-data mt-0.5">{value}</div>
  </div>
);

const ReasonTags = ({ reasons }) =>
  reasons?.length ? (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="match-reasons">
      {reasons.map((r) => (
        <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 font-medium">
          {r} ✓
        </span>
      ))}
    </div>
  ) : null;

const GapTags = ({ gaps }) =>
  gaps?.length ? (
    <div className="flex flex-wrap gap-1.5" data-testid="match-gaps">
      {gaps.map((g) => (
        <span key={g} className="text-[10px] px-2 py-0.5 rounded-full bg-[#ef4444]/15 text-[#ff6b6b] border border-[#ef4444]/30 font-medium">
          {g} ✗
        </span>
      ))}
    </div>
  ) : null;

const MatchBadge = ({ pct }) => {
  if (pct == null) return null;
  const color = pct >= 80 ? "#25D366" : pct >= 50 ? "#DFFF00" : "#8A8F9E";
  return (
    <span
      className="text-[11px] font-bold font-data px-2.5 py-1 rounded-full border shrink-0"
      style={{ color, borderColor: `${color}66`, backgroundColor: `${color}1a` }}
      data-testid={`match-pct-${pct}`}
    >
      {pct}% Match
    </span>
  );
};

const NearMatches = ({ items, role }) => {
  if (!items?.length) return null;
  const isProvider = role === "provider";
  return (
    <div data-testid="near-matches">
      <div className="rounded-xl border border-[#DFFF00]/30 bg-[#DFFF00]/5 px-4 py-3 mb-5 text-sm text-[#DFFF00] flex items-start gap-2.5" data-testid="near-matches-nudge">
        <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          No exact matches yet. Try lowering the minimum sea time or broadening acceptable COC countries.
          Here are the closest {isProvider ? "candidates" : "vacancies"}:
        </span>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-white/10 bg-[#0A0B12] p-5" data-testid={`near-card-${it.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-[#12131A] border border-white/10 flex items-center justify-center">
                {isProvider ? <Anchor className="w-4 h-4 text-[#FF5A00]" /> : <Building2 className="w-4 h-4 text-[#0066FF]" />}
              </div>
              <div>
                <div className="font-heading font-bold leading-tight">{isProvider ? it.rank : it.company_name}</div>
                {!isProvider && <div className="text-xs text-[#8A8F9E]">{it.rank}</div>}
                {!isProvider && it.is_rpsl === false && (
                  <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#f59e0b] border border-[#f59e0b]/40 font-semibold" data-testid={`non-rpsl-badge-${it.id}`}>Non-RPSL Company</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Vessel" value={it.vessel_type} />
              <Field label="Sea time" value={it.sea_time} />
              <Field label="COC" value={Array.isArray(it.coc) ? it.coc.join(", ") : it.coc} />
              <Field label="US Visa" value={it.us_visa} />
              <Field label="Available" value={it.availability} />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#8A8F9E] mb-1.5">Doesn't match on</div>
            <GapTags gaps={it.gaps} />
          </div>
        ))}
      </div>
    </div>
  );
};

const EligibilityGate = ({ referralCode, onEligible }) => {
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  const link = `${window.location.origin}/?ref=${referralCode || ""}`;

  const load = () => api.get("/eligibility").then((r) => setElig(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const share = async (type) => {
    try {
      await api.post("/referral/share", { type });
    } catch { /* ignore */ }
    const text = `Join me on CrewingConnect — find shipping jobs & crew fast! ${link}`;
    window.open(waLink("", text), "_blank");
    load();
  };

  const claim = async () => {
    setBusy(true);
    try {
      await api.post("/referral/claim");
      toast.success("Free access unlocked — thanks for sharing! 🎉");
      onEligible?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Share more to unlock");
      load();
    } finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      await api.post("/register/pay");
      toast.success("Registered! (₹100 paid — demo)");
      onEligible?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  if (!elig) return null;
  const g = elig.group_shares, c = elig.contact_shares;
  const canClaim = g >= 2 || c >= 10;

  return (
    <div className="rounded-2xl border border-[#0066FF]/40 bg-gradient-to-br from-[#0066FF]/10 to-transparent p-5 sm:p-6 mb-6" data-testid="eligibility-gate">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-[#FF5A00]" />
        <h3 className="font-heading font-extrabold text-lg">Unlock company names & contacts</h3>
      </div>
      <p className="text-sm text-[#8A8F9E] mb-5">
        You can browse all matching vacancies free. To reveal <b>company name & contact details</b>, simply share CrewingConnect — it's <b className="text-[#25D366]">free &amp; instant</b>.
      </p>

      <div className="max-w-2xl mx-auto">
        {/* One-time registration — top, muted */}
        <div className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid="registration-section">
          <div className="text-sm font-semibold mb-1">One-time Registration</div>
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="text-2xl font-heading font-extrabold text-[#8A8F9E] line-through decoration-[#ef4444] decoration-2">₹100</span>
            <span className="text-sm text-[#DFFF00] font-semibold">Skip it with a free share below ↓</span>
          </div>
          <p className="text-xs text-[#8A8F9E] mb-4">Prefer not to share? Pay once to unlock all company contacts.</p>
          <Button variant="outline" className="w-full rounded-full border-white/15 bg-transparent hover:bg-white/5 gap-2" disabled={busy} onClick={pay} data-testid="pay-register-btn">
            <IndianRupee className="w-4 h-4" /> Pay ₹100 &amp; register
          </Button>
        </div>

        {/* OR divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs font-bold text-[#8A8F9E] uppercase tracking-[0.3em]">OR</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {/* Share — highlighted & recommended */}
        <div className="rounded-xl border-2 border-[#25D366]/60 bg-[#25D366]/5 p-4" data-testid="share-section">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Gift className="w-5 h-5 text-[#25D366]" />
            <span className="font-heading font-extrabold text-lg sm:text-xl text-[#25D366] drop-shadow-[0_0_10px_rgba(37,211,102,0.35)]">Simply Share for Free — Share &amp; Visibility</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DFFF00] text-black font-bold">RECOMMENDED</span>
          </div>
          <p className="text-xs text-[#8A8F9E] mb-3">Share with <b>2 WhatsApp groups</b> or <b>10 contacts</b> — unlock instantly, <b className="text-[#25D366]">no payment</b>.</p>
          <div className="flex gap-2 mb-3">
            <div className="flex-1 text-center rounded-lg bg-black/30 border border-white/10 py-2">
              <div className="font-data text-lg">{g}/2</div>
              <div className="text-[10px] text-[#8A8F9E]">Groups</div>
            </div>
            <div className="flex-1 text-center rounded-lg bg-black/30 border border-white/10 py-2">
              <div className="font-data text-lg">{c}/10</div>
              <div className="text-[10px] text-[#8A8F9E]">Contacts</div>
            </div>
          </div>
          <div className="flex gap-2 mb-2">
            <Button size="sm" className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-black font-semibold gap-1 rounded-full" onClick={() => share("group")} data-testid="share-group-btn">
              <Users className="w-3.5 h-3.5" /> Share to group
            </Button>
            <Button size="sm" variant="outline" className="flex-1 border-[#25D366]/40 bg-transparent hover:bg-[#25D366]/10 text-[#25D366] gap-1 rounded-full" onClick={() => share("contact")} data-testid="share-contact-btn">
              <Share2 className="w-3.5 h-3.5" /> Send to contact
            </Button>
          </div>
          <Button className="w-full rounded-full gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:bg-white/10 disabled:text-[#c9cdd6] disabled:opacity-100" disabled={!canClaim || busy} onClick={claim} data-testid="claim-free-btn">
            <CheckCircle2 className="w-4 h-4" /> {canClaim ? "Claim free access" : "Share more to unlock"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CompanyAlerts = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/wa-alerts").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data?.alerts?.length) return null;
  return (
    <div className="rounded-xl border border-[#25D366]/40 bg-[#25D366]/5 p-4 mb-5" data-testid="company-wa-alerts">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle className="w-4 h-4 text-[#25D366]" />
        <span className="font-heading font-bold text-sm text-[#25D366]">WhatsApp alerts sent to you</span>
        {data.demo && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#DFFF00] text-black font-bold">DEMO</span>}
      </div>
      <div className="space-y-1.5">
        {data.alerts.slice(0, 6).map((a, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-xs" data-testid="company-wa-alert-row">
            <span className="text-[#E7EAF0]">{a.message}</span>
            <span className="text-[10px] text-[#8A8F9E] font-data shrink-0">{String(a.created_at).slice(0, 10)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Matches = ({ browse = false, companyId = null, onChange }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(null);
  const strike = useStrikePrices();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);

  const load = () => api.get(browse ? (companyId ? `/browse?company=${companyId}` : "/browse") : "/matches").then((r) => {
    setRes(r.data);
    if (r.data.role === "provider") api.get("/company/status").then((s) => setStatus(s.data)).catch(() => {});
  }).catch(() => setRes({ role: null, matches: [] }));
  useEffect(() => { load(); }, [browse, companyId]);

  useEffect(() => {
    const refresh = () => api.get("/company/status").then((s) => setStatus(s.data)).catch(() => {});
    window.addEventListener("mc-status-changed", refresh);
    window.addEventListener("focus", refresh);
    return () => { window.removeEventListener("mc-status-changed", refresh); window.removeEventListener("focus", refresh); };
  }, []);

  const startFree = async (id) => {
    setBusy(id);
    try {
      await api.post("/company/free-launch");
      toast.success("Free launch month activated — contact unlocks included!");
      window.dispatchEvent(new Event("mc-status-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start free offer");
    } finally { setBusy(null); }
  };

  const unlock = async (id) => {
    setBusy(id);
    try {
      const r = await api.post("/unlock-cv", { seeker_id: id });
      if (r.data.needs_payment) {
        await payFor("cv_unlock", { seeker_id: id });
        toast.success("CV unlocked! (₹29 paid)");
      } else {
        toast.success(r.data.amount ? "CV unlocked! (₹29 paid)" : "CV unlocked — free!");
      }
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Unlock failed");
    } finally {
      setBusy(null);
    }
  };

  const downloadCv = async (id, name) => {
    try {
      const res = await api.get(`/cv/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cv-${(name || "crew").replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Standard CV downloaded");
    } catch (e) {
      toast.error("Download failed — check your plan or daily limit");
    }
  };

  if (!res) return <Empty text="Loading matches..." />;
  if (!res.role) return <Empty text="Complete a profile or requirement in the chat to see your matches." />;

  const alerts = !browse ? <MatchAlerts newCount={res.new_matches || 0} role={res.role} onSeen={load} /> : null;
  const companyAlerts = res.role === "provider" && !browse ? <CompanyAlerts /> : null;

  // Provider view — candidate CVs with locked identity/contact
  if (res.role === "provider") {
    if (!res.matches.length)
      return (
        <>
          {alerts}
          {companyAlerts}
          {res.near_matches?.length
            ? <NearMatches items={res.near_matches} role="provider" />
            : <Empty text="No matching candidates yet. Check back soon — new crew join daily." />}
        </>
      );
    return (
      <>
        {alerts}
        {companyAlerts}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="matches-provider">
        {res.matches.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-[#0A0B12] p-5 flex flex-col" data-testid={`match-card-${c.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#12131A] border border-white/10 flex items-center justify-center">
                  <Anchor className="w-4 h-4 text-[#FF5A00]" />
                </div>
                <div className="font-heading font-bold">{c.rank}</div>
              </div>
              <div className="flex items-center gap-2">
                {c.boosted && <Badge className="bg-[#DFFF00] text-black text-[10px]">BOOSTED</Badge>}
                <MatchBadge pct={c.match_pct} />
              </div>
            </div>

            {c.summary && <p className="text-xs text-[#8A8F9E] mb-4">{c.summary}</p>}

            <ReasonTags reasons={c.match_reasons} />

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Vessel" value={c.vessel_type} />
              <Field label="Sea time" value={c.sea_time} />
              <Field label="COC" value={c.coc} />
              <Field label="US Visa" value={c.us_visa} />
              <Field label="Available" value={c.availability} />
            </div>

            <div className={`rounded-lg border p-3 mb-1 relative ${c.unlocked ? "border-[#25D366]/40 bg-[#25D366]/5" : "border-white/10 bg-black/30"}`}>
              {!c.unlocked && (
                <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm rounded-lg">
                  <Lock className="w-5 h-5 text-[#FF5A00]" />
                </div>
              )}
              <div className={!c.unlocked ? "blur-[3px] select-none" : ""}>
                <Field label="Name" value={c.name} />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Field label="Mobile" value={c.mobile} />
                  <Field label="Email" value={c.email} />
                </div>
              </div>
            </div>
            {!c.unlocked && <p className="text-[10px] text-[#8A8F9E] mb-3 mt-1.5">Name, contact, INDOS, CDC, passport &amp; photo hidden until unlocked</p>}
            {c.unlocked && <div className="mb-3" />}

            {c.unlocked ? (
              <div className="flex gap-2">
                <Button disabled className="flex-1 bg-[#25D366]/20 text-[#25D366] border border-[#25D366]/40 gap-2 rounded-full" data-testid={`unlocked-${c.id}`}>
                  <Unlock className="w-4 h-4" /> Unlocked
                </Button>
                <Button
                  className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-black font-semibold gap-2 rounded-full"
                  onClick={() => window.open(waLink(c.mobile, buildContactMessage({ crewName: c.name, position: c.rank, companyEmail: res.requirement?.email, companyName: res.requirement?.company_name })), "_blank")}
                  data-testid={`whatsapp-${c.id}`}
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </Button>
                {status?.subscribed && (
                  <Button
                    variant="outline"
                    className="border-[#FF5A00]/40 bg-transparent hover:bg-[#FF5A00]/10 text-[#FF7A2E] rounded-full px-3"
                    onClick={() => downloadCv(c.id, c.name)}
                    data-testid={`download-cv-${c.id}`}
                    title="Download Standard CV (PDF)"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ) : (status && !status.subscribed && !status.free_launch_used) ? (
              <div className="flex items-center gap-3" data-testid={`free-launch-unlock-${c.id}`}>
                <span className={`font-heading font-extrabold text-2xl text-white ${strikeCls(strike)} shrink-0`} data-testid={`unlock-original-price-${c.id}`}>₹29</span>
                <Button
                  className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-black font-bold rounded-full gap-2"
                  disabled={busy === c.id}
                  onClick={() => startFree(c.id)}
                  data-testid={`get-started-free-unlock-btn-${c.id}`}
                >
                  <Unlock className="w-4 h-4" /> {busy === c.id ? "Activating..." : "Get Started Free"}
                </Button>
              </div>
            ) : (
              <Button
                className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-full gap-2"
                disabled={busy === c.id}
                onClick={() => unlock(c.id)}
                data-testid={`unlock-btn-${c.id}`}
              >
                {(status?.subscribed || res.free_unlocks_left > 0) ? <Unlock className="w-4 h-4" /> : <IndianRupee className="w-4 h-4" />}
                {busy === c.id ? "Processing..." : (status?.subscribed ? "Unlock free" : (res.free_unlocks_left > 0 ? `Unlock free · ${res.free_unlocks_left} left` : "Unlock · ₹29"))}
              </Button>
            )}
          </div>
        ))}
        </div>
      </>
    );
  }

  // Seeker view — matching vacancies (company name/contact gated)
  const eligible = res.eligible;
  return (
    <div data-testid="matches-seeker">
      {alerts}
      {res.company_filter && (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-[#FF5A00]/40 bg-[#FF5A00]/10 px-4 py-3" data-testid="company-filter-banner">
          <div className="text-sm text-[#E7EAF0]">
            Showing postings by <b className="text-[#FF7A2E]">{res.company_name || "this company"}</b>
          </div>
          <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent gap-1.5 h-8 px-3 text-xs" onClick={() => navigate("/dashboard")} data-testid="clear-company-filter">
            <X className="w-3.5 h-3.5" /> Show all
          </Button>
        </div>
      )}
      {!res.company_filter && <GetNoticedFaster referralCode={res.profile?.referral_code} onUnlocked={load} />}
      {eligible && (
        <div className="mb-6 flex items-center gap-2 text-sm text-[#25D366] rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 px-4 py-3" data-testid="eligible-banner">
          <CheckCircle2 className="w-4 h-4" /> Company names &amp; contacts are unlocked. Tap WhatsApp to reach out directly.
        </div>
      )}
      {!res.matches.length ? (
        res.near_matches?.length
          ? <NearMatches items={res.near_matches} role="seeker" />
          : <Empty text="No matching vacancies yet. Check back soon — new jobs are posted daily." />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {res.matches.map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-[#0A0B12] p-5" data-testid={`match-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#12131A] border border-white/10 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-[#0066FF]" />
                  </div>
                  <div>
                    <div className={`font-heading font-bold leading-tight ${!eligible ? "text-[#8A8F9E]" : ""}`}>{p.company_name}</div>
                    <div className="text-xs text-[#8A8F9E]">{p.rank}</div>
                    {p.is_rpsl === false && (
                      <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#f59e0b] border border-[#f59e0b]/40 font-semibold" data-testid={`non-rpsl-badge-${p.id}`}>Non-RPSL Company</span>
                    )}
                  </div>
                </div>
                <MatchBadge pct={p.match_pct} />
              </div>
              {p.summary && <p className="text-xs text-[#8A8F9E] mb-4">{p.summary}</p>}
              <ReasonTags reasons={p.match_reasons} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vessel" value={p.vessel_type} />
                <Field label="Min. sea time" value={p.sea_time} tag={p.sea_time_priority} />
                <Field label="COC" value={Array.isArray(p.coc) ? p.coc.join(", ") : p.coc} />
                <Field label="US Visa" value={p.us_visa} />
                <Field label="Joining" value={p.availability} tag={p.availability_priority} />
              </div>
              {eligible ? (
                <Button
                  className="w-full mt-4 bg-[#25D366] hover:bg-[#1da851] text-black font-semibold gap-2 rounded-full"
                  onClick={() => window.open(waLink(p.company_mobile, `Hi, I found your ${p.rank} vacancy on CrewingConnect and I'd like to apply.`), "_blank")}
                  data-testid={`seeker-whatsapp-${p.id}`}
                >
                  <MessageCircle className="w-4 h-4" /> Contact {p.company_name} on WhatsApp
                </Button>
              ) : (
                <div className="mt-4 flex items-center gap-2 text-xs text-[#FF5A00]">
                  <Lock className="w-3.5 h-3.5" /> Company name &amp; contact locked
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
